import { ref } from 'vue'
import { defineStore } from 'pinia'
import { getMenuApi, type MenuClassNode } from '@/api/common'
import { LEGACY_CONTEXT, STORAGE_KEYS } from '@/settings'
import menuTreeFallback from '@/mock/menu-tree.json'

/**
 * 菜单与权限
 *
 * 关键设计：后端 /getMenu 返回的 menuUrl 是旧页面地址
 * （例如 /html/sitemanage/pileList.html），这里做一次地址转换，
 * 使【后端菜单表完全不需要改动】即可驱动新的 Vue 路由。
 * 这一条是压缩迁移工期的核心：菜单/权限体系零改造。
 */

export interface MenuChild {
  id: string
  title: string
  /** 旧页面地址，保留用于 iframe 过渡与问题定位 */
  legacyUrl: string
  /** 转换后的 Vue 路由地址 */
  path: string
  icon?: string
}

export interface MenuGroup {
  title: string
  /** 旧工程 menuClassName */
  className: string
  children: MenuChild[]
}

/** 旧页面地址 -> Vue 路由地址 */
export function legacyUrlToRoute(menuUrl: string, menuId?: string, menuSubId?: string): string {
  if (!menuUrl) return '/index'
  let p = menuUrl
  // 去掉 contextPath（如 /hp-cloud）与 /html 前缀
  p = p.replace(new RegExp(`^${LEGACY_CONTEXT.replace('/', '\\/')}`), '')
  p = p.replace(/^\/html/, '')
  p = p.replace(/\.html$/, '')
  if (!p || p === '/') p = '/index'
  if (p === '/index') return '/index'
  const query: string[] = []
  if (menuId) query.push(`menuId=${menuId}`)
  if (menuSubId) query.push(`menuSubId=${menuSubId}`)
  return query.length ? `${p}?${query.join('&')}` : p
}

export const usePermissionStore = defineStore('permission', () => {
  const menuGroups = ref<MenuGroup[]>([])
  const loaded = ref(false)

  /** 把后端菜单结构转换为前端菜单结构 */
  function transform(list: MenuClassNode[]): MenuGroup[] {
    return (list || [])
      .filter((g) => g && Array.isArray(g.menuList) && g.menuList.length > 0)
      .map((g) => ({
        title: g.menuClassName,
        className: g.menuClassName,
        children: g.menuList.map((m) => ({
          id: String(m.id),
          title: m.menuName,
          legacyUrl: m.menuUrl,
          path: legacyUrlToRoute(m.menuUrl, String(m.id), m.defaultJumpMenuId),
          icon: m.menuPhotoId
        }))
      }))
  }

  /** 从后端加载菜单；接口不可用时退回本地兜底菜单，保证前端可独立开发 */
  async function loadMenu(force = false) {
    if (loaded.value && !force) return menuGroups.value

    // 旧工程把菜单 HTML 缓存在 sessionStorage，这里沿用缓存思路
    if (!force) {
      const cached = sessionStorage.getItem(STORAGE_KEYS.MENU)
      if (cached) {
        try {
          menuGroups.value = JSON.parse(cached)
          loaded.value = true
          return menuGroups.value
        } catch {
          /* 缓存损坏则重新拉取 */
        }
      }
    }

    try {
      const list = await getMenuApi()
      menuGroups.value = transform(list)
    } catch {
      // 兜底：用扫描旧工程得到的菜单树，便于前端离线开发
      menuGroups.value = (menuTreeFallback as any[]).map((m) => ({
        title: m.name,
        className: m.key,
        children: m.children.map((c: any) => ({
          id: c.key,
          title: c.name,
          legacyUrl: `/html/${c.key}.html`,
          path: c.path,
          icon: ''
        }))
      }))
    }

    sessionStorage.setItem(STORAGE_KEYS.MENU, JSON.stringify(menuGroups.value))
    loaded.value = true
    return menuGroups.value
  }

  function reset() {
    menuGroups.value = []
    loaded.value = false
    sessionStorage.removeItem(STORAGE_KEYS.MENU)
  }

  return { menuGroups, loaded, loadMenu, reset, legacyUrlToRoute }
})
